export const UI_LANGUAGES = ['de', 'en'] as const;
export type UiLanguage = (typeof UI_LANGUAGES)[number];

export interface LanguageOption {
  code: string;
  flag: string;
  en: string;
  de: string;
}

export const LANGUAGE_OPTIONS: readonly LanguageOption[] = [
  { code: 'ar', flag: 'sa', en: 'Arabic', de: 'Arabisch' },
  { code: 'bg', flag: 'bg', en: 'Bulgarian', de: 'Bulgarisch' },
  { code: 'zh', flag: 'cn', en: 'Chinese', de: 'Chinesisch' },
  { code: 'hr', flag: 'hr', en: 'Croatian', de: 'Kroatisch' },
  { code: 'cs', flag: 'cz', en: 'Czech', de: 'Tschechisch' },
  { code: 'da', flag: 'dk', en: 'Danish', de: 'Dänisch' },
  { code: 'nl', flag: 'nl', en: 'Dutch', de: 'Niederländisch' },
  { code: 'en', flag: 'gb', en: 'English', de: 'Englisch' },
  { code: 'et', flag: 'ee', en: 'Estonian', de: 'Estnisch' },
  { code: 'fa', flag: 'ir', en: 'Persian', de: 'Persisch' },
  { code: 'fi', flag: 'fi', en: 'Finnish', de: 'Finnisch' },
  { code: 'fr', flag: 'fr', en: 'French', de: 'Französisch' },
  { code: 'de', flag: 'de', en: 'German', de: 'Deutsch' },
  { code: 'el', flag: 'gr', en: 'Greek', de: 'Griechisch' },
  { code: 'he', flag: 'il', en: 'Hebrew', de: 'Hebräisch' },
  { code: 'hi', flag: 'in', en: 'Hindi', de: 'Hindi' },
  { code: 'hu', flag: 'hu', en: 'Hungarian', de: 'Ungarisch' },
  { code: 'id', flag: 'id', en: 'Indonesian', de: 'Indonesisch' },
  { code: 'it', flag: 'it', en: 'Italian', de: 'Italienisch' },
  { code: 'ja', flag: 'jp', en: 'Japanese', de: 'Japanisch' },
  { code: 'ko', flag: 'kr', en: 'Korean', de: 'Koreanisch' },
  { code: 'lv', flag: 'lv', en: 'Latvian', de: 'Lettisch' },
  { code: 'lt', flag: 'lt', en: 'Lithuanian', de: 'Litauisch' },
  { code: 'no', flag: 'no', en: 'Norwegian', de: 'Norwegisch' },
  { code: 'pl', flag: 'pl', en: 'Polish', de: 'Polnisch' },
  { code: 'pt', flag: 'pt', en: 'Portuguese', de: 'Portugiesisch' },
  { code: 'ro', flag: 'ro', en: 'Romanian', de: 'Rumänisch' },
  { code: 'ru', flag: 'ru', en: 'Russian', de: 'Russisch' },
  { code: 'sr', flag: 'rs', en: 'Serbian', de: 'Serbisch' },
  { code: 'sk', flag: 'sk', en: 'Slovak', de: 'Slowakisch' },
  { code: 'sl', flag: 'si', en: 'Slovenian', de: 'Slowenisch' },
  { code: 'es', flag: 'es', en: 'Spanish', de: 'Spanisch' },
  { code: 'sv', flag: 'se', en: 'Swedish', de: 'Schwedisch' },
  { code: 'th', flag: 'th', en: 'Thai', de: 'Thailändisch' },
  { code: 'tr', flag: 'tr', en: 'Turkish', de: 'Türkisch' },
  { code: 'uk', flag: 'ua', en: 'Ukrainian', de: 'Ukrainisch' },
  { code: 'ur', flag: 'pk', en: 'Urdu', de: 'Urdu' },
  { code: 'vi', flag: 'vn', en: 'Vietnamese', de: 'Vietnamesisch' },
] as const;

export const MIME_TYPES = [
  'audio/mpeg',
  'audio/aac',
  'audio/aacp',
  'audio/ogg',
  'audio/opus',
  'audio/webm',
  'audio/flac',
] as const;

export const COUNTRY_CODES = (
  'ad ae af ag ai al am ao aq ar as at au aw ax az ba bb bd be bf bg bh bi bj bl bm bn bo bq br bs bt bv bw by bz ca cc cd cf cg ch ci ck cl cm cn co cr cu cv cw cx cy cz de dj dk dm do dz ec ee eg eh er es et fi fj fk fm fo fr ga gb gd ge gf gg gh gi gl gm gn gp gq gr gs gt gu gw gy hk hm hn hr ht hu id ie il im in io iq ir is it je jm jo jp ke kg kh ki km kn kp kr kw ky kz la lb lc li lk lr ls lt lu lv ly ma mc md me mf mg mh mk ml mm mn mo mp mq mr ms mt mu mv mw mx my mz na nc ne nf ng ni nl no np nr nu nz om pa pe pf pg ph pk pl pm pn pr ps pt pw py qa re ro rs ru rw sa sb sc sd se sg sh si sj sk sl sm sn so sr ss st sv sx sy sz tc td tf tg th tj tk tl tm tn to tr tt tv tw tz ua ug um us uy uz va vc ve vg vi vn vu wf ws ye yt za zm zw'
).split(' ');

export const TIME_ZONE_OPTIONS = [
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Warsaw',
  'Europe/Kyiv',
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Jerusalem',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
] as const;

export function flagEmoji(code: string): string {
  if (!/^[a-z]{2}$/i.test(code)) return '🌐';
  return [...code.toUpperCase()]
    .map((character) => String.fromCodePoint(127397 + character.charCodeAt(0)))
    .join('');
}

export function languageOption(code: string): LanguageOption | undefined {
  return LANGUAGE_OPTIONS.find((option) => option.code === code);
}

export function languageName(code: string, uiLanguage: UiLanguage): string {
  const option = languageOption(code);
  return option?.[uiLanguage] || code.toUpperCase();
}
